import React from 'react';
import { Image, ImageProps } from '@fluentui/react-components';
import { FALLBACK_IMAGE } from '../../Constants/FallbackImage';

interface ProductImageProps extends ImageProps {
    fallback?: string;
}

// Fluent v9 <Image> that falls back when the source is missing OR fails to load at
// runtime (404, broken URL, decode error). The <img> `error` event — forwarded by
// Fluent v9's Image — is the only reliable signal for a broken thumbnail (the HTTP
// status itself is opaque to the img element, but any load failure is the right trigger
// for a fallback). Guards against an infinite loop if the fallback itself fails, and
// resets when `src` changes so a recycled row/card doesn't stay stuck on the fallback
// after scrolling to a product with a good image.
export const ProductImage: React.FC<ProductImageProps> = ({ src, fallback = FALLBACK_IMAGE, onError, ...rest }) => {
    const resolved = src && src !== '' ? src : fallback;
    const [current, setCurrent] = React.useState(resolved);
    // Re-sync when the incoming source changes (recycled row/card) so a previously-failed
    // image doesn't keep the fallback after scrolling to a product with a good image.
    React.useEffect(() => { setCurrent(resolved); }, [resolved]);
    return (
        <Image
            {...rest}
            src={current}
            onError={(e) => {
                if (current !== fallback) setCurrent(fallback);
                onError?.(e);
            }}
        />
    );
};
